import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  Dropdown, DropdownToggle, DropdownMenu, Popover, PopoverBody,
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { ReportContext } from '../context/ReportProvider';
import { formValuesToFoundryParams, sortByColumn } from '../Forms/hooks/useReport/util';
import { store } from '../../../stores/store';

const fileTypeMap: Record<string, string> = {
  CSV: 'csv',
  Excel: 'xlsx',
};

interface ExportDropdownProps {
  objectType: string
}

const ExportDropdown: React.FC<ExportDropdownProps> = ({ objectType }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const exportWrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileTypes = ['CSV', 'Excel'];
  const exportUrl = process.env.REACT_APP_EXPORT_URL ?? '';
  const reportContext = useContext(ReportContext);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const toggleDropdown = () => setDropdownOpen(prevState => !prevState);
  const isDisabled = (reportContext?.reportTotalCount ?? 0) <= 0;
  const initialParams = {
    departmentId: [store.commonStore.departmentId].filter((id): id is number => id !== undefined),
    hospitalId: [store.commonStore.hospitalId].filter((id): id is number => id !== undefined)
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filterParams = reportContext?.reportFilter && Object.keys(reportContext.reportFilter).length > 0
    ? reportContext.reportFilter
    : initialParams;

  const sortParams = sortByColumn(
    typeof reportContext?.reportFilter === 'object' && reportContext?.reportFilter !== null
      ? reportContext.reportFilter
      : {}
  ) ?? undefined;

  const handleDownload = async (type: string) => {
       if (!navigator.onLine) {
         toast.error('No internet connection. Please check your network and try again.'); 
         return;
    }
    const infoToastId = toast.info('Your file is being prepared...');

    const fileExtension = fileTypeMap[type];
    setIsLoading(true);
    setDropdownOpen(false);
    const requestBody =
    {
      where: {
        type: 'and',
        value: formValuesToFoundryParams(filterParams, 'export'),   
      },
      select: [],
      excludeRid: true,
      orderBy: sortParams,
      pageSize: reportContext?.reportPageSize,
    };
    
    try {
      const response = await 
        axios.post(exportUrl, requestBody, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          params: {
            type: fileExtension,
            objectType: objectType,
          },
        });

      const charCodes = response.data?.dataContent?.content?.data;
      const metadata = response.data?.dataContent?.metadata;
      const contentType = metadata?.contentType;
      const filename = metadata?.name || `${objectType + new Date().toLocaleDateString('en-CA').replace(/-/g, '')}.${fileExtension}`;

      if (!charCodes || !Array.isArray(charCodes) || !contentType) {
        toast.error('Invalid file data or metadata received.');
        console.error('Invalid file data or metadata received.');
        return;
      }

      const uint8Array = new Uint8Array(charCodes);
      const decodedText = new TextDecoder().decode(uint8Array);

      const blob =
        contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          ? new Blob([Uint8Array.from(atob(decodedText), c => c.charCodeAt(0))], {
            type: contentType,
          })
          : contentType === 'text/csv'
            ? new Blob([decodedText], { type: 'text/csv;charset=utf-8' })
            : (() => {
              console.error(`Unsupported content type: ${contentType} `);
              return null;
            })();
      if (!blob) return;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.dismiss(infoToastId);
      toast.error('There was an error in preparing your file, please try again.');
      console.error('Download failed:', error);
    } finally {
      setDropdownOpen(false);
      setIsLoading(false);
    }
  };

  return (
    <div ref={dropdownRef} className='ml-4'>
      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} direction='down'>
        <div
          ref={exportWrapperRef}
          onMouseEnter={() => isDisabled && setIsPopoverOpen(true)}
          onMouseLeave={() => setIsPopoverOpen(false)}
          style={{ display: 'inline-block' }}
        >
          <DropdownToggle caret color='primary' outline disabled={isDisabled}>
            {isLoading ? 'Downloading...' : 'Export'}
          </DropdownToggle>
        </div>
        <DropdownMenu right style={{ minWidth: '100px' }}>
          {fileTypes.map(type => (
            <button
              key={type}
              className='dropdown-item'
              onClick={() => handleDownload(type)}
              disabled={isLoading}
            >
              {type}
            </button>
          ))}
        </DropdownMenu>
      </Dropdown>

      <Popover placement='top' isOpen={isPopoverOpen} target={exportWrapperRef}>
        <PopoverBody>No data to export</PopoverBody>
      </Popover>
    </div>
  );
};

export default ExportDropdown;
